import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalHospital as MedicalIcon,
  Paid as PaidIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';
import {
  archiveMedicalTransportOrder,
  fetchMedicalTransportOrders
} from '../../redux/slices/medicalTransportOrdersSlice';
import {
  APPROVAL_STATUS_OPTIONS,
  BILLING_STATUS_OPTIONS,
  DOCUMENT_STATUS_OPTIONS,
  formatDate,
  getOption
} from './medicalTransportOrderOptions';

const StatusChip = ({ options, value }) => {
  const option = getOption(options, value);
  return (
    <Chip
      label={option.label}
      color={option.color}
      size="small"
      variant={option.color === 'default' ? 'outlined' : 'filled'}
    />
  );
};

const MedicalTransportOrdersList = () => {
  const dispatch = useDispatch();
  const { orders, isLoading, error } = useSelector(state => state.medicalTransportOrders);
  const [searchTerm, setSearchTerm] = useState('');
  const [archiveTarget, setArchiveTarget] = useState(null);

  useEffect(() => {
    dispatch(fetchMedicalTransportOrders());
  }, [dispatch]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter(order => (
      order.patientName?.toLowerCase().includes(term) ||
      order.healthInsuranceName?.toLowerCase().includes(term) ||
      order.approvalNumber?.toLowerCase().includes(term) ||
      order.prescriptionNumber?.toLowerCase().includes(term) ||
      order.pickupAddress?.toLowerCase().includes(term) ||
      order.destinationAddress?.toLowerCase().includes(term)
    ));
  }, [orders, searchTerm]);

  const handleArchive = async () => {
    if (!archiveTarget) return;
    await dispatch(archiveMedicalTransportOrder(archiveTarget.id));
    setArchiveTarget(null);
  };

  if (isLoading && orders.length === 0) {
    return <Loading message="Verordnungen werden geladen..." />;
  }

  if (error && orders.length === 0) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Verordnungen"
        message={error}
        onRetry={() => dispatch(fetchMedicalTransportOrders())}
      />
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Krankenfahrten / Verordnungen"
        description="Verordnungsfälle für wiederkehrende Krankenfahrten verwalten"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Krankenfahrten', path: '/krankenfahrten' }
        ]}
        actionButton={{
          label: 'Verordnung anlegen',
          icon: <AddIcon />,
          path: '/krankenfahrten/neu'
        }}
        secondaryButtons={[
          {
            label: 'Abrechnung',
            icon: <PaidIcon />,
            path: '/krankenfahrten/abrechnung'
          }
        ]}
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          label="Suchen"
          placeholder="Patient, Kasse, Genehmigung, Strecke..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          fullWidth
          size="small"
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Patient</TableCell>
              <TableCell>Krankenkasse</TableCell>
              <TableCell>Gültigkeit</TableCell>
              <TableCell align="center">Kontingent</TableCell>
              <TableCell>Genehmigung</TableCell>
              <TableCell>Dokumente</TableCell>
              <TableCell>Abrechnung</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box sx={{ py: 4 }}>
                    <MedicalIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">
                      Keine Verordnungen gefunden
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map(order => (
                <TableRow key={order.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {order.patientName || 'Unbekannt'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.patientPhone || 'Keine Telefonnummer'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.healthInsuranceName || 'Nicht gesetzt'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.insuranceNumber ? `Vers.-Nr. ${order.insuranceNumber}` : 'Keine Versichertennummer'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(order.validFrom)} - {formatDate(order.validUntil)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.destinationAddress || 'Kein Ziel'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${order.usedRides || 0}/${order.maxRides || 0}`}
                      color={(order.maxRides || 0) > 0 && (order.usedRides || 0) >= (order.maxRides || 0) ? 'error' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip options={APPROVAL_STATUS_OPTIONS} value={order.approvalStatus} />
                  </TableCell>
                  <TableCell>
                    <StatusChip options={DOCUMENT_STATUS_OPTIONS} value={order.documentStatus} />
                  </TableCell>
                  <TableCell>
                    <StatusChip options={BILLING_STATUS_OPTIONS} value={order.billingStatus} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Details">
                      <IconButton component={Link} to={`/krankenfahrten/${order.id}`}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Bearbeiten">
                      <IconButton component={Link} to={`/krankenfahrten/${order.id}/bearbeiten`}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Archivieren">
                      <IconButton color="error" onClick={() => setArchiveTarget(order)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteConfirmDialog
        open={Boolean(archiveTarget)}
        title="Verordnung archivieren"
        content={`Soll die Verordnung für ${archiveTarget?.patientName || 'diesen Patienten'} archiviert werden? Bestehende Fahrten bleiben unverändert.`}
        confirmText="Archivieren"
        onConfirm={handleArchive}
        onCancel={() => setArchiveTarget(null)}
      />
    </Box>
  );
};

export default MedicalTransportOrdersList;
